import { useCallback, useEffect, useState } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { messengerApi } from '../lib/messengerApi';
import type { PresenceStatus } from '../types';

// Task #1660 — hook unificato presence.
// - heartbeat 30s socket
// - bootstrap lista online via REST `/messenger/presence`
// - patch event-driven da socket `chat:presence` (status esteso)
// - expone `presenceMap: Record<userId, status>` + setter self

export function usePresence(tenantId: string | null | undefined) {
  const { socket, joinRoom, leaveRoom, on, off } = useSocket();
  const [presenceMap, setPresenceMap] = useState<Record<string, PresenceStatus>>({});
  const [myStatus, setMyStatus] = useState<PresenceStatus>('online');

  // Bootstrap
  useEffect(() => {
    if (!tenantId) return;
    messengerApi.getPresence().then((d) => {
      if (d.success && Array.isArray(d.data)) {
        const m: Record<string, PresenceStatus> = {};
        d.data.forEach((uid) => { m[uid] = 'online'; });
        setPresenceMap(m);
      }
    });
  }, [tenantId]);

  // Socket heartbeat
  useEffect(() => {
    if (!tenantId || !socket) return;
    joinRoom(`messenger:${tenantId}`);
    socket.emit('messenger:heartbeat');
    const t = window.setInterval(() => socket.emit('messenger:heartbeat'), 30_000);
    return () => {
      window.clearInterval(t);
      leaveRoom(`messenger:${tenantId}`);
    };
  }, [tenantId, socket, joinRoom, leaveRoom]);

  // Live patches
  useEffect(() => {
    const handler = (data: { userId: string; status: PresenceStatus | 'offline' }) => {
      setPresenceMap((prev) => {
        const next = { ...prev };
        if (data.status === 'offline') delete next[data.userId];
        else next[data.userId] = data.status;
        return next;
      });
    };
    on('chat:presence', handler);
    return () => { off('chat:presence', handler); };
  }, [on, off]);

  const updateMyStatus = useCallback((s: PresenceStatus) => {
    setMyStatus(s);
  }, []);

  return { presenceMap, myStatus, setMyStatus: updateMyStatus };
}
